/*
 * Store format (EBO / LFS) rules for the UI — what a store code looks like and which
 * stores a job may use. Mirrors server/modules/stores/storeFormat.js; keep the two in step.
 *
 *   EBO — 4 digits, e.g. "1101"
 *   LFS — letters/digits joined by dashes, e.g. "LF-PT-PV01"
 *
 * Stores and jobs saved before `storeFormat` existed come back without it; they are all
 * EBO, so always go through normaliseStoreFormat() instead of reading the field directly.
 */

export const STORE_FORMATS = ['EBO', 'LFS'];
export const DEFAULT_STORE_FORMAT = 'EBO';

const LFS_CODE_MAX_LENGTH = 15;

export const STORE_CODE_RULES = {
  EBO: {
    label: 'Code (4 digit numerical)',
    example: '1101',
    maxLength: 4,
    inputMode: 'numeric',
    // Keep only what the scheme allows, so typing can't produce an invalid code.
    sanitize: (value) => String(value || '').replace(/\D/g, '').slice(0, 4),
    isValid: (code) => /^\d{4}$/.test(code),
    message: 'EBO Store Code must be exactly 4 numeric digits (e.g. 1101).',
  },
  LFS: {
    label: 'Store Code (e.g. LF-PT-PV01)',
    example: 'LF-PT-PV01',
    maxLength: LFS_CODE_MAX_LENGTH,
    inputMode: 'text',
    sanitize: (value) =>
      String(value || '')
        .toUpperCase()
        .replace(/[^A-Z0-9-]/g, '')
        .slice(0, LFS_CODE_MAX_LENGTH),
    // Must contain a letter: a digits-only code ("6565") is an EBO code.
    isValid: (code) =>
      code.length <= LFS_CODE_MAX_LENGTH &&
      /^[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*$/.test(code) &&
      /[A-Za-z]/.test(code),
    message: `LFS Store Code must include letters, use dashes between parts and be up to ${LFS_CODE_MAX_LENGTH} characters (e.g. LF-PT-PV01).`,
  },
};

/** Any input → 'EBO' | 'LFS'. Missing, blank or unknown values are EBO. */
export function normaliseStoreFormat(value) {
  const format = String(value || '').trim().toUpperCase();
  return STORE_FORMATS.includes(format) ? format : DEFAULT_STORE_FORMAT;
}

export function getStoreCodeRule(format) {
  return STORE_CODE_RULES[normaliseStoreFormat(format)];
}

/** null when `code` is a valid store code for `format`, otherwise a user-facing message. */
export function storeCodeError(code, format) {
  const rule = getStoreCodeRule(format);
  return rule.isValid(String(code || '').trim()) ? null : rule.message;
}

/** The stores a job of `format` may use. */
export function filterStoresByFormat(stores, format) {
  const target = normaliseStoreFormat(format);
  return (stores || []).filter((store) => normaliseStoreFormat(store?.storeFormat) === target);
}
